import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { CartItem, CartOption, CartOptionItem } from '../types/cart';
import { MenuItem as MenuItemType, MenuOption } from '../types/menu';
import { Restaurant } from '../types/restaurant';
import { getMenuDetail } from '../api/menuService';
import { getRestaurantDetails } from '../api/restaurantService';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import CartBottomSheet from '../components/CartBottomSheet';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import ImageWithFallback from '../components/ImageWithFallback';
import { getStatusMessages } from '../config/constants';
import useTranslation from '../hooks/useTranslation';
import { useOperationTimeCheck } from '../hooks/useOperationTime';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import { formatPrice as formatPriceWithCurrency, SupportedCurrency, getCurrencyFromLanguage, getPriceInfo } from '../utils/currencyUtils';
import { getCurrentLanguage, getLocalizedValue } from '../config/languageConfig';
import ServiceClosedModal from '../components/ServiceClosedModal';

// 메뉴 옵션을 장바구니 옵션으로 변환하는 함수 추가
const convertMenuOptionToCartOption = (menuOption: MenuOption): CartOption => {
  const optionItems: CartOptionItem[] = menuOption.menuOptionItems.map(item => ({
    id: item.id,
    name: item.name,
    price: item.price // 원화(KRW) 그대로 사용
  }));

  return {
    id: menuOption.id,
    name: menuOption.name,
    price: 0, // 옵션 자체 가격은 0으로 설정
    optionItems: optionItems
  };
};

const MenuDetail: React.FC = () => {
  const { restaurantId = '', menuId = '' } = useParams<{ restaurantId: string; menuId: string }>();
  const navigate = useNavigate();
  const { cart, addToCart, updateItemQuantity, canceledItemId, getMinOrderAmount, setRestaurantMinOrderAmount } = useCart();
  const { t } = useTranslation();
  const { checkAndExecute } = useOperationTimeCheck();
  const { checkAddressAndExecute } = useAddressAffordance();
  
  // 현재 언어에 기반한 기본 통화 설정
  const currentLang = getCurrentLanguage();
  const selectedCurrency: SupportedCurrency = getCurrencyFromLanguage(currentLang);
  
  // 다국어 상태 메시지 가져오기
  const STATUS_MESSAGES = getStatusMessages();
  
  // 메뉴 데이터 상태
  const [menuItem, setMenuItem] = useState<MenuItemType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // 레스토랑 정보 상태
  const [restaurantName, setRestaurantName] = useState<string>('');
  const [minOrderAmount, setMinOrderAmount] = useState<number | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  
  // 수량 상태
  const [quantity, setQuantity] = useState<number>(1);
  
  // 운영시간 외 모달 상태
  const [showServiceClosedModal, setShowServiceClosedModal] = useState<boolean>(false);
  
  // 품절 여부 (available이 명시적으로 false인 경우)
  const isSoldOut = menuItem?.available === false;

  // 프로모션 정보 가져오기
  const priceInfo = useMemo(() => {
    if (!menuItem) return null;
    return getPriceInfo(menuItem, selectedCurrency);
  }, [menuItem, selectedCurrency]);
  
  // 메뉴 아이템에서 현재 언어에 맞는 가격 계산
  const itemPrice = useMemo(() => {
    if (!menuItem || !priceInfo) return 0;
    
    // 프로모션이 있으면 할인 가격, 없으면 일반 가격
    return priceInfo.hasPromotion ? priceInfo.promotionPrice : priceInfo.regularPrice;
  }, [menuItem, priceInfo]);
  
  // 현재 언어에 맞는 이름 가져오기
  const displayName = useMemo(() => {
    if (!menuItem) return '';
    return getLocalizedValue(menuItem.name, {
      en: menuItem.nameEn
    });
  }, [menuItem]);
  
  // 현재 언어에 맞는 설명 가져오기
  const displayDescription = useMemo(() => {
    if (!menuItem) return '';
    
    let description = getLocalizedValue(menuItem.description, {
      en: menuItem.descriptionEn
    });
    
    // 대괄호로 감싸진 형태인 경우 대괄호를 제거하고 안의 내용만 추출
    if (description.match(/^\[(.*)\]$/)) {
      description = description.replace(/^\[(.*)\]$/, '$1');
    }
    
    // 유효한 설명인지 확인 (빈 문자열이거나 [] 형태가 아닌 경우)
    if (!description || description.match(/^\[]$/)) {
      return '';
    }
    
    return description;
  }, [menuItem]);
  
  // 총 가격 계산 (수량 × 가격)
  const totalPrice = useMemo(() => {
    return formatPriceWithCurrency(itemPrice * quantity, selectedCurrency);
  }, [itemPrice, quantity, selectedCurrency]);
  
  // 결제 페이지로 이동
  const handleCheckout = () => {
    checkAddressAndExecute(() => {
      navigate('/cart');
    });
  };
  
  // 메뉴 데이터 가져오기
  useEffect(() => {
    const fetchMenuData = async () => {
      if (!restaurantId || !menuId) {
        setError(t('menu.invalidId'));
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const menuData = await getMenuDetail(restaurantId, menuId);
        
        if (!menuData) {
          setError(t('menu.notFound'));
          setLoading(false);
          return;
        }
        
        setMenuItem(menuData);
        setRestaurantName(menuData.name);
        
        try {
          // 가게 정보 조회하여 최소 주문 금액 가져오기
          const restaurant = await getRestaurantDetails(restaurantId);
          
          if (restaurant) {
            // 레스토랑 객체 저장
            setRestaurant(restaurant);
            
            // 서버 응답에서 각 통화별 최소 주문 금액 가져오기
            const minOrderAmounts = {
              krw: restaurant.minOrderAmountKrw || restaurant.minOrderAmount || 0,
              usd: restaurant.minOrderAmountUsd || restaurant.minOrderAmount || 0,
              jpy: restaurant.minOrderAmountJpy || restaurant.minOrderAmount || 0,
              cny: restaurant.minOrderAmountCny || restaurant.minOrderAmount || 0,
              twd: restaurant.minOrderAmountTwd || restaurant.minOrderAmount || 0,
            };
            
            // 현재 설정된 값과 다를 때만 업데이트
            const currentMinAmount = getMinOrderAmount();
            const currency = getCurrencyFromLanguage(currentLang);
            let currentCurrencyAmount: number;
            switch (currency) {
              case 'USD': currentCurrencyAmount = minOrderAmounts.usd; break;
              case 'JPY': currentCurrencyAmount = minOrderAmounts.jpy; break;
              case 'CNY': currentCurrencyAmount = minOrderAmounts.cny; break;
              case 'TWD': currentCurrencyAmount = minOrderAmounts.twd; break;
              case 'KRW':
              default: currentCurrencyAmount = minOrderAmounts.krw; break;
            }
            
            if (currentMinAmount !== currentCurrencyAmount) {
              // CartContext에 최소 주문 금액 설정
              setRestaurantMinOrderAmount(minOrderAmounts);
              
              // 로컬 상태에도 저장
              setMinOrderAmount(currentCurrencyAmount);
            }
          }
        } catch (apiError) {
          console.error('Error fetching restaurant details:', apiError);
          // 레스토랑 정보 조회 실패 시 하드코딩 기본값을 쓰지 않음.
          // 최소주문금액은 장바구니의 calculate-order 응답(실효 값)으로 판정/안내됨.
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching menu details:', err);
        setError(STATUS_MESSAGES.error.menus || t('menu.fetchFailed'));
        setLoading(false);
      }
    };
    
    fetchMenuData();
  }, [restaurantId, menuId, currentLang]);
  
  // 언어 변경 시 CartContext에서 최소 주문 금액 가져오기
  useEffect(() => {
    // CartContext에서 최소 주문 금액 가져오기
    const contextMinOrderAmount = getMinOrderAmount();
    if (contextMinOrderAmount !== null) {
      setMinOrderAmount(contextMinOrderAmount);
    }
  }, [currentLang]);
  
  // 카트에서 현재 아이템의 수량 가져오기
  useEffect(() => {
    if (!menuItem || !cart) {
      setQuantity(0);
      return;
    }
    
    if (cart.restaurantId !== restaurantId) {
      setQuantity(0);
      return;
    }
    
    const cartItem = cart.items.find(item => item.id === menuId);
    setQuantity(cartItem ? cartItem.quantity : 0);
  }, [cart, menuItem, menuId, restaurantId]);
  
  // 취소된 아이템 감지하여 수량 초기화
  useEffect(() => {
    if (canceledItemId && canceledItemId === menuId) {
      if (!cart) {
        setQuantity(0);
        return;
      }
      
      if (cart.restaurantId !== restaurantId) {
        setQuantity(0);
        return;
      }
      
      const cartItem = cart.items.find(item => item.id === menuId);
      setQuantity(cartItem ? cartItem.quantity : 0);
    }
  }, [canceledItemId, menuId, cart, restaurantId]);
  
  // 수량 증가
  const increaseQuantity = () => {
    // 품절 메뉴는 장바구니에 담을 수 없음
    if (isSoldOut) return;

    checkAddressAndExecute(() => {
      checkAndExecute(() => {
        const newQuantity = quantity + 1;
        setQuantity(newQuantity);
        updateCartItem(newQuantity);
      }, () => {
        setShowServiceClosedModal(true);
      });
    }, 'cart');
  };
  
  // 수량 감소
  const decreaseQuantity = () => {
    if (quantity > 0) {
      checkAndExecute(() => {
        const newQuantity = quantity - 1;
        setQuantity(newQuantity);
        updateCartItem(newQuantity);
      }, () => {
        setShowServiceClosedModal(true);
      });
    }
  };
  
  // 장바구니 업데이트
  const updateCartItem = (newQuantity: number) => {
    if (!menuItem) return;
    
    if (newQuantity === 0) {
      // 수량이 0이면 아이템 제거
      updateItemQuantity(menuId, 0);
    } else if (!cart || cart.restaurantId !== restaurantId || !cart.items.find(item => item.id === menuId)) {
      // 처음 추가하는 경우 (장바구니에 해당 아이템이 없거나 다른 레스토랑 카트가 있을 경우)
      
      // 메뉴 옵션을 장바구니 옵션으로 변환
      const cartOptions: CartOption[] = menuItem.menuOptions.map(option => 
        convertMenuOptionToCartOption(option)
      );
      
      const cartItem: CartItem = {
        id: menuId,
        name: displayName,
        price: priceInfo?.hasPromotion ? priceInfo.promotionPrice : priceInfo?.regularPrice || 0, // 프로모션 가격 적용
        quantity: newQuantity,
        options: cartOptions,
        image: menuItem.image,
        // 모든 통화별 가격 정보 포함
        priceKrw: menuItem.priceKrw,
        priceUsd: menuItem.priceUsd,
        priceJpy: menuItem.priceJpy,
        priceCny: menuItem.priceCny,
        priceTwd: menuItem.priceTwd
      };
      
      // 가게 다국어 정보를 담은 객체 생성
      const restaurantInfo = {
        restaurantName: restaurantName,
        restaurantNameEn: restaurant?.nameEn || restaurantName,
        restaurantNameJa: restaurant?.nameJa ?? undefined,
        restaurantNameZhCn: restaurant?.nameZhCn ?? undefined,
        restaurantNameZhTw: restaurant?.nameZhTw ?? undefined,
        restaurantNameKo: restaurant?.nameKo ?? undefined
      };
      
      // 레스토랑 이름 검증 (메뉴 이름과 같으면 경고)
      if (restaurantInfo.restaurantName === displayName) {
        console.warn('Warning: Restaurant name matches menu item name. This may indicate incorrect data.', {
          restaurantName: restaurantInfo.restaurantName,
          menuItemName: displayName
        });
      }
      
      addToCart(restaurantId, restaurantInfo, cartItem);
    } else {
      // 수량 업데이트
      updateItemQuantity(menuId, newQuantity);
    }
  };
  
  // 카트에 추가
  const addToCartHandler = () => {
    if (quantity === 0) {
      increaseQuantity();
    } else {
      // 카트에 추가하고 가게 상세 페이지로 이동
      updateCartItem(quantity);
      navigate(`/restaurant/${restaurantId}`);
    }
  };
  
  // 로딩 중 표시
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('menu.item')} />
        <div className="p-4">
          <LoadingState message={STATUS_MESSAGES.loading.menus} />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  // 에러 표시
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.title')} />
        <div className="p-4">
          <ErrorState message={error} onRetry={() => window.location.reload()} />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  // 정상 컨텐츠 표시
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <BackHeader title={t('menu.item')} />
      
      <div className="flex-grow overflow-auto pb-40">
        {/* 메뉴 이미지 */}
        <div className="w-full h-56 bg-gray-200 relative">
          <ImageWithFallback
            src={menuItem?.image || ''}
            alt={displayName}
            className={`w-full h-full object-cover ${isSoldOut ? 'opacity-40 grayscale' : ''}`}
            fallback="https://source.unsplash.com/random/800x800/?food"
          />
          {isSoldOut && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <span className="text-white text-2xl font-bold">{t('menu.soldOut')}</span>
            </div>
          )}
        </div>
        
        {/* 메뉴 정보 */}
        <div className="p-4 bg-white rounded-t-xl -mt-4 relative z-10 shadow-sm flex flex-col items-center">
          <div className="w-full max-w-md">
            <h1 className="text-2xl font-bold mb-2 text-center">{displayName}</h1>
            
            {displayDescription && (
              <p className="text-gray-600 mb-6 text-base text-center">{displayDescription}</p>
            )}
            
            {/* 가격 표시 */}
            <div className="mb-6 flex flex-col items-center">
              {priceInfo?.hasPromotion ? (
                <>
                  {/* 원래 가격 (위에 작게) */}
                  <div className="text-gray-400 text-sm line-through mb-0.5">
                    {priceInfo.formattedRegularPrice}
                  </div>
                  {/* 할인율과 할인 가격 */}
                  <div className="flex items-center space-x-2">
                    <span className="text-red-500 font-bold text-lg">{priceInfo.formattedDiscountRate}</span>
                    <span className="text-black font-bold text-xl">{priceInfo.formattedPromotionPrice}</span>
                  </div>
                </>
              ) : (
                <div className="text-black font-bold text-xl">{priceInfo?.formattedRegularPrice}</div>
              )}
            </div>
          </div>
          
          {/* 수량 조절 UI */}
          <div className="py-4 border-t border-b border-gray-200 w-full max-w-md">
            <div className="flex items-center justify-between">
              <span className="text-gray-800 font-semibold text-lg">{t('quantity')}</span>
              
              <div className="flex items-center">
                <button
                  className={`w-8 h-8 flex items-center justify-center border rounded-md ${
                    quantity > 0 ? 'border-gray-300 text-gray-700' : 'border-gray-200 text-gray-400'
                  }`}
                  onClick={decreaseQuantity}
                  disabled={quantity === 0}
                  aria-label="Decrease quantity"
                >
                  <span className="text-xl font-medium">−</span>
                </button>
                <span className="mx-4 text-lg font-medium min-w-[20px] text-center">{quantity}</span>
                <button
                  className={`w-8 h-8 flex items-center justify-center border rounded-md ${
                    isSoldOut
                      ? 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-red-500 text-white border-red-500'
                  }`}
                  onClick={increaseQuantity}
                  disabled={isSoldOut}
                  aria-label="Increase quantity"
                >
                  <span className="text-xl font-medium">+</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 하단 액션 영역 — NavigationBar(fixed bottom-0 z-[9999]) 위에 두어 히트 영역이 겹치지 않게 함 */}
      <div className="fixed bottom-16 left-1/2 z-20 w-full max-w-mobile -translate-x-1/2 transform border-t border-gray-100 bg-white p-4 shadow-top">
        <div className="w-full flex items-center justify-between">
          <div className="mr-4">
            <div className="text-gray-600 text-sm">{t('menu.totalPrice')}</div>
            <div className="text-xl font-bold">{totalPrice}</div>
          </div>
          
          {isSoldOut ? (
            <button
              className="flex-1 py-3 px-4 rounded-lg font-semibold text-gray-500 bg-gray-200 cursor-not-allowed"
              disabled
            >
              {t('menu.soldOut')}
            </button>
          ) : quantity > 0 ? (
            <div className="flex-1 flex space-x-2">
              <button
                className="flex-1 py-3 px-3 rounded-lg font-semibold text-white bg-red-500"
                onClick={addToCartHandler}
              >
                {t('menu.updateCart')}
              </button>
              <button
                className="flex-1 py-3 px-3 rounded-lg font-semibold text-white bg-blue-500"
                onClick={handleCheckout}
              >
                {t('nav.cart')}
              </button>
            </div>
          ) : (
            <button
              className="flex-1 py-3 px-4 rounded-lg font-semibold text-white bg-red-500"
              onClick={addToCartHandler}
            >
              {t('menu.addToCart')}
            </button>
          )}
        </div>
      </div>
      
      {/* 푸터 탐색 바 */}
      <div className="h-16" />
      <NavigationBar />
      
      {/* 카트 정보 */}
      <CartBottomSheet
        minOrderAmount={minOrderAmount}
        onCheckout={handleCheckout}
        restaurantId={restaurantId}
        currency={selectedCurrency}
      />

      {/* 운영시간 외 모달 */}
      <ServiceClosedModal 
        isOpen={showServiceClosedModal}
        onClose={() => setShowServiceClosedModal(false)}
      />
    </div>
  );
};

export default MenuDetail;
